/**
 * One line per coding agent on a machine (R-ON-9, ADR 0099 decision 3): what the readiness report
 * found (Machine.readiness), what the machine's agent says it can install
 * (state.ext['agent-install'].status) and the latest install the hub started there, as one state
 * with its status label: Not installed, Installing, Installed, sign in, Ready, Install failed,
 * Needs Node.js, Install in a terminal (Codex on Windows: Orgus does not run its installer, R-EN-3).
 */
import { AGENT_INSTALL_PHASE_LABEL, AGENT_SIGN_IN_COMMAND, CODEX_WINDOWS_INSTALL_COMMAND, INSTALLABLE_AGENT_KINDS, agentKindLabel, isInstallableAgentKind } from '@orgus/protocol';
import type { AgentInstallError, AgentInstallHubState, AgentInstallRecord, AgentKind, Machine } from '@orgus/protocol';
import { formatPercent } from '@orgus/ui';
import { shortVersion } from './readiness-model.ts';

export type AgentLineState = 'not-installed' | 'installing' | 'sign-in' | 'ready' | 'installed' | 'failed' | 'needs-node' | 'in-terminal' | 'unavailable' | 'unknown';

export interface AgentLine {
  kind: AgentKind;
  state: AgentLineState;
  /** The status label, icon plus this. */
  label: string;
  /** One meta fact: the version, or the install phase and progress. */
  meta: string | null;
  /** Orgus can install it here: the Install action is offered (to people who may install). */
  installable: boolean;
  /** The latest install the hub started for this agent on this machine. */
  record: AgentInstallRecord | null;
  error: AgentInstallError | null;
}

export const AGENT_LINE_LABEL: Record<AgentLineState, string> = {
  'not-installed': 'Not installed',
  installing: 'Installing',
  'sign-in': 'Installed, sign in',
  ready: 'Ready',
  installed: 'Installed',
  failed: 'Install failed',
  'needs-node': 'Needs Node.js',
  'in-terminal': 'Install in a terminal',
  unavailable: 'Not available here',
  unknown: 'Sign-in unknown',
};

/** The latest install record per agent on one machine. */
export function latestInstalls(state: AgentInstallHubState | null | undefined, machineId: string): Map<AgentKind, AgentInstallRecord> {
  const out = new Map<AgentKind, AgentInstallRecord>();
  for (const r of state?.installs ?? []) {
    if (r.machineId !== machineId) continue;
    const prev = out.get(r.kind);
    if (!prev || r.requestedAt > prev.requestedAt) out.set(r.kind, r);
  }
  return out;
}

function progressMeta(r: AgentInstallRecord): string | null {
  if (!r.phase) return null;
  const label = AGENT_INSTALL_PHASE_LABEL[r.phase];
  if (r.phase === 'downloading' && r.total && r.bytes !== undefined) return `${label}, ${formatPercent(Math.min(1, r.bytes / r.total))}`;
  return label;
}

/**
 * Every agent line for a machine: the harnesses its readiness report found, in that order, then
 * the agents Orgus can install that the report does not list. An agent the machine reports
 * nothing about is left out.
 */
export function agentLines(m: Pick<Machine, 'id' | 'readiness'>, state: AgentInstallHubState | null | undefined): AgentLine[] {
  const status = state?.status?.[m.id];
  const records = latestInstalls(state, m.id);
  const kinds: AgentKind[] = [];
  for (const h of m.readiness?.harnesses ?? []) if (!kinds.includes(h.kind)) kinds.push(h.kind);
  for (const k of INSTALLABLE_AGENT_KINDS) if (!kinds.includes(k) && status?.kinds.some((x) => x.kind === k)) kinds.push(k);
  return kinds.map((kind) => {
    const h = m.readiness?.harnesses.find((x) => x.kind === kind);
    const s = status?.kinds.find((x) => x.kind === kind);
    const record = records.get(kind) ?? null;
    // A success the machine has not reported in a status since still counts as installed.
    const justInstalled = record?.state === 'installed' && (record.finishedAt ?? 0) >= (status?.checkedAt ?? 0);
    const installed = !!h?.installed || !!s?.installed || justInstalled;
    const version = shortVersion(h?.version ?? s?.version ?? (record?.state === 'installed' ? record.version : null));
    const canInstall = isInstallableAgentKind(kind) && !!s?.installable && !installed;
    const line = (state: AgentLineState, meta: string | null, installable = false): AgentLine => ({ kind, state, label: AGENT_LINE_LABEL[state], meta, installable, record, error: state === 'failed' ? (record?.error ?? null) : null });
    if (record?.state === 'installing') return line('installing', progressMeta(record));
    if (installed) {
      if (h?.signIn === 'signed-in') return line('ready', version);
      if (h?.signIn === 'signed-out') return line('sign-in', version);
      // No sign-in state (an agent without the readiness probe): right after an install it still needs one.
      if (record?.state === 'installed') return line('sign-in', version);
      return line(h ? 'unknown' : 'installed', version);
    }
    if (record?.state === 'failed') return line('failed', null, canInstall);
    if (s?.blocked === 'needs-node') return line('needs-node', null);
    if (s?.blocked === 'install-in-terminal') return line('in-terminal', null);
    if (s?.blocked === 'unsupported-platform') return line('unavailable', null);
    return line('not-installed', null, canInstall);
  });
}

/** The lines Add machine and Getting started show: only where something is missing or happening. */
export function linesToAct(lines: readonly AgentLine[]): AgentLine[] {
  return lines.filter((l) => l.state !== 'ready' && l.state !== 'installed' && l.state !== 'unknown');
}

/** The one sentence under a line that needs words: why an install failed, how to sign in, what Gemini CLI needs, the command to run in a terminal. */
export function lineSentence(l: AgentLine, machineName: string): string | null {
  const name = agentKindLabel(l.kind);
  if (l.state === 'failed' && l.error) return `${l.error.why} ${l.error.next}`;
  if (l.state === 'failed') return `${name} was not installed on ${machineName}. Try again.`;
  if (l.state === 'sign-in' && isInstallableAgentKind(l.kind)) return `Sign in to ${name} on ${machineName}: run ${AGENT_SIGN_IN_COMMAND[l.kind]} there${l.kind === 'claude-code' ? ', or sign a Claude account in from Settings, Accounts' : ''}.`;
  if (l.state === 'needs-node') return `${name} needs Node.js 20 or later: install it from nodejs.org on ${machineName}, then install ${name}.`;
  if (l.state === 'in-terminal') return `Install ${name} from your own terminal on ${machineName}: run ${CODEX_WINDOWS_INSTALL_COMMAND} in PowerShell.`;
  return null;
}
